import { roundTo2 } from '@fastre/core/helperFunctions/number'
import { ListingContractSchema } from '@fastre/core/schemas/contract'
import { InternalSaleListingSchema, listingEnabledForUser } from '@fastre/core/schemas/saleListing'
import { VendorType } from '@fastre/core/schemas/vendor'
import { zodResolver } from '@hookform/resolvers/zod'
import { AddRounded, CheckRounded, CloseRounded } from '@mui/icons-material'
import { Box, Button, Checkbox, Divider, FormControl, FormLabel, Option, Stack, Typography } from '@mui/joy'
import { useApi } from 'api'
import { useListingsApi, useSpecialConditionSnippetsApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Input, { SlotInput } from 'components/input'
import Select from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { MobileFriendlyStack } from 'components/stack'
import UnsavedModal from 'components/unsavedModal'
import { addDays, differenceInDays, format } from 'date-fns'
import { useIsMobile } from 'helperFunctions/pageSize'
import { RenderListingValues } from 'listings/additionalValues'
import DocusignFormStepper from 'listings/docusignFormStepper'
import { EditModeProvider, useEditMode } from 'listings/editMode'
import { omit, prop } from 'ramda'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useBlocker } from 'react-router'
import { useListingContext } from '../listingProvider'
import VendorList from '../vendorList'
import ConditionRender, { getDueDate } from './conditionRender'
import EditSolicitor from './editSolicitorModal'

const SectionHead = ({ children }: { children: React.ReactNode }) => (
    <Typography
        level="h4"
        sx={{
            pt: 2,
        }}
    >
        {children}
    </Typography>
)

const DepositPaidRender = ({ depositNum }: { depositNum: number }) => {
    const { listing } = useListingContext()
    const isMobile = useIsMobile()

    return (
        <FormControl>
            <FormLabel>Paid</FormLabel>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isMobile ? undefined : 'center',
                    height: '24px',
                }}
            >
                {listing.balanceSheet?.find(x => x.externalRef == `deposit${depositNum}`)?.paid ? (
                    <CheckRounded color="success" />
                ) : (
                    <CloseRounded />
                )}
            </Box>
        </FormControl>
    )
}

const Contract = forwardRef(({}, ref) => {
    const { listing, setListing } = useListingContext()
    const showSnack = useShowSnack()
    const api = useApi()
    const { user } = useUserData()
    const usersApi = useUsersApi()
    const listingsApi = useListingsApi(false)
    const snippetsApi = useSpecialConditionSnippetsApi()
    const { editMode } = useEditMode()

    const [loading, setLoading] = useState(false)
    const [vendorType, setVendorType] = useState<VendorType>(
        listing?.contract?.buyers[0]?.vendorType ?? 'individual',
    )

    const [ocrLoading, setOcrLoading] = useState(false)

    const enabledForUser = listingEnabledForUser(listing, usersApi.data, user)

    const formEnabled =
        user.permissions.includes('listings.fullControl') ||
        (listing.contractForm?.rei == undefined &&
            listing.contractForm?.esign == undefined &&
            user.permissions.includes('listings.edit') &&
            enabledForUser)

    const formDisabled = !formEnabled

    const formFullyDisabled =
        !user.permissions.includes('listings.fullControl') &&
        !(enabledForUser && user.permissions.includes('listings.edit'))

    const hookForm = useForm<ListingContractSchema>({
        defaultValues: {
            buyers: (listing as any).contract?.buyers ?? [
                {
                    vendorType: 'individual',
                    name: '',
                    email: '',
                    phone: '',
                },
            ],
            ...((listing as any).contract ?? {}),
            initialDepositDue: (listing as any).contract?.initialDepositDue ?? 1,
            settlementDateDays: (listing as any).contract?.settlementDateDays ?? 30,
            additionalDetails: listing.additionalDetails ?? {},
        },
        resolver: zodResolver(ListingContractSchema),
    })

    const { register, control, handleSubmit, formState, getValues, setValue, trigger, reset, watch } =
        hookForm

    const salePrice = useWatch({ control, name: 'salePrice' })
    const contractDate = useWatch({ control, name: 'contractDate' })
    const initialDepositDue = useWatch({ control, name: 'initialDepositDue' })
    const balanceDepositDue = useWatch({ control, name: 'balanceDepositDue' })
    const buyersAgent = useWatch({ control, name: 'buyersAgent' })
    const commissionOverride =
        useWatch({ control, name: 'commissionOverride' }) ?? listing.contract?.commissionOverride
    const commissionOverrideValue = useWatch({ control, name: 'commissionOverrideValue' })
    const buildingAndPestRequired = useWatch({ control, name: 'buildingAndPestRequired' })
    const requiresFinance = useWatch({ control, name: 'requiresFinance' })
    const settlementDateDays = useWatch({ control, name: 'settlementDateDays' })

    const [voidModalOpen, setVoidModalOpen] = useState(false)

    const {
        fields: buyers,
        append: appendBuyer,
        remove: removeBuyer,
    } = useFieldArray({
        control,
        name: 'buyers',
    })

    const {
        fields: conditions,
        append: appendCondition,
        prepend: prependCondition,
        remove: removeCondition,
    } = useFieldArray({
        control,
        name: 'contractConditions',
    })

    const resetForm = (listing: InternalSaleListingSchema) =>
        reset({
            ...listing.contract,
            additionalDetails: listing.additionalDetails,
        })

    const onSubmit: SubmitHandler<ListingContractSchema> = async ({ additionalDetails, ...data }) => {
        setLoading(true)
        try {
            const updatedListing = await api
                .post(`/listing/sale/${listing.listingId}/upsertcontract`, {
                    contract: data,
                    additionalDetails,
                })
                .then(prop('data'))
            setListing(updatedListing)
            resetForm(updatedListing)
            showSnack('Contract saved', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving contract data', 'danger')
            throw e // Re-throw the error so handleSubmitPromise can catch it
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitPromise = async (): Promise<boolean> => {
        try {
            await new Promise<void>((resolve, reject) => {
                handleSubmit(
                    async data => {
                        try {
                            await onSubmit(data)
                            resolve()
                        } catch (error) {
                            reject(error)
                        } finally {
                            listingsApi.refreshSpecific(listing.listingId)
                        }
                    },
                    error => {
                        reject(error)
                    },
                )()
            })
            return true
        } catch (error) {
            return false
        }
    }

    const saveForm = async (): Promise<boolean> => {
        if (formDisabled) {
            setLoading(true)
            try {
                const updatedListing = await api
                    .post(`/listing/sale/${listing.listingId}/updatelockedcontract`, {
                        additionalDetails: getValues('additionalDetails'),
                        contractDate: getValues('contractDate'),
                    })
                    .then(prop('data'))
                setListing(updatedListing)
                reset(updatedListing.contract)
                listingsApi.refreshSpecific(listing.listingId)
                showSnack('Contract saved', 'success')
                return true
            } catch (e) {
                console.error(e)
                showSnack('Error saving contract', 'danger')
                return false
            } finally {
                setLoading(false)
            }
        } else {
            return await handleSubmitPromise()
        }
    }

    useImperativeHandle(ref, () => ({
        saveForm,
        resetForm: () => resetForm(listing),
    }))

    if (Object.keys(formState.errors).length > 0) {
        console.log('errors', formState.errors)
    }

    useEffect(() => {
        // Force refresh on dirty states
        console.log('formstate.isdirty', formState.isDirty)
        console.log('formState.dirtyFields', formState.dirtyFields)
    }, [JSON.stringify(formState.dirtyFields), formState.isDirty])

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        //return false
        console.log('dirty fields', formState.dirtyFields)
        console.log('is dirty', formState.isDirty)
        return (
            currentLocation.pathname !== nextLocation.pathname &&
            formState.isDirty &&
            Object.keys(formState.dirtyFields).length > 0
        )
    })

    return (
        <>
            <FormProvider {...hookForm}>
                <form
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Stack gap={2}>
                        <Stack
                            gap={2}
                            sx={{ width: '500px' }}
                        >
                            <Controller
                                name="salePrice"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Price"
                                        type="dollar"
                                        startDecorator="$"
                                        {...field}
                                        formControlProps={{
                                            sx: { flex: 1 },
                                        }}
                                    />
                                )}
                            />
                            <Stack gap={2}>
                                <Controller
                                    name="contractDate"
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label="Contract Date"
                                            type="date"
                                            {...field}
                                            formControlProps={{
                                                sx: { flex: 1 },
                                            }}
                                            fullWidth
                                            disabled={
                                                listing.contractForm?.esign?.formStatus != 'manual upload'
                                            }
                                        />
                                    )}
                                />
                            </Stack>

                            <Input
                                label="Commission Inc"
                                value={roundTo2(
                                    ((listing.commission ?? 0) *
                                        (salePrice ?? listing.contract?.salePrice ?? 0)) /
                                        100,
                                )}
                                type="dollar"
                                startDecorator="$"
                                readOnly
                                formControlProps={{
                                    sx: { flex: 1 },
                                }}
                                fullWidth
                                disabled
                                sx={{
                                    '> input': {
                                        textDecoration: commissionOverride ? 'line-through' : undefined,
                                    },
                                }}
                            />
                            {user.permissions.includes('listings.fullControl') && (
                                <Stack gap={2}>
                                    <Input
                                        label="Commission Exc"
                                        value={roundTo2(((listing.commission ?? 0) * salePrice) / 100 / 1.1)}
                                        type="dollar"
                                        startDecorator="$"
                                        readOnly
                                        formControlProps={{
                                            sx: { flex: 1 },
                                        }}
                                        fullWidth
                                        disabled
                                        sx={{
                                            '> input': {
                                                textDecoration: commissionOverride
                                                    ? 'line-through'
                                                    : undefined,
                                            },
                                        }}
                                    />
                                    {(editMode || commissionOverride) && (
                                        <>
                                            <Controller
                                                name="commissionOverride"
                                                control={control}
                                                disabled={formDisabled}
                                                render={({ field }) => (
                                                    <Stack
                                                        direction="row"
                                                        gap={2}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: '15px',
                                                            }}
                                                        >
                                                            Commission Override
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Checkbox
                                                                //label="Commission Override"
                                                                {...omit(['value'], field)}
                                                                checked={field.value}
                                                                onChange={e => {
                                                                    field.onChange(e.target.checked)
                                                                    if (!e.target.checked) {
                                                                        setValue(
                                                                            'commissionOverrideValue',
                                                                            undefined,
                                                                            {
                                                                                shouldDirty: true,
                                                                            },
                                                                        )
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                )}
                                            />
                                            {commissionOverride && (
                                                <>
                                                    <Controller
                                                        name="commissionOverrideValue"
                                                        control={control}
                                                        disabled={formDisabled}
                                                        render={field => (
                                                            <Box
                                                                sx={{
                                                                    opacity: commissionOverride ? 1 : 0,
                                                                    pointerEvents: commissionOverride
                                                                        ? 'auto'
                                                                        : 'none',
                                                                }}
                                                            >
                                                                <SlotInput
                                                                    label="Commission Override Inc"
                                                                    type="dollar"
                                                                    startDecorator="$"
                                                                    {...field}
                                                                    formControlProps={{
                                                                        sx: { flex: 1 },
                                                                    }}
                                                                    fullWidth
                                                                    tabIndex={
                                                                        commissionOverride ? undefined : -1
                                                                    }
                                                                />
                                                            </Box>
                                                        )}
                                                    />
                                                    <Controller
                                                        name="commissionOverrideValue"
                                                        control={control}
                                                        disabled={formDisabled}
                                                        render={field => (
                                                            <Box
                                                                sx={{
                                                                    opacity: commissionOverride ? 1 : 0,
                                                                    pointerEvents: commissionOverride
                                                                        ? 'auto'
                                                                        : 'none',
                                                                }}
                                                            >
                                                                <SlotInput
                                                                    label="Commission Override Exc"
                                                                    type="dollar"
                                                                    startDecorator="$"
                                                                    {...omit(['value'], field)}
                                                                    value={
                                                                        commissionOverrideValue
                                                                            ? roundTo2(
                                                                                  commissionOverrideValue /
                                                                                      1.1,
                                                                              )
                                                                            : undefined
                                                                    }
                                                                    onChange={(val: any) =>
                                                                        field.field.onChange(
                                                                            val ? roundTo2(val * 1.1) : val,
                                                                        )
                                                                    }
                                                                    formControlProps={{
                                                                        sx: { flex: 1 },
                                                                    }}
                                                                    tabIndex={
                                                                        commissionOverride ? undefined : -1
                                                                    }
                                                                    fullWidth
                                                                />
                                                            </Box>
                                                        )}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                        <Divider sx={{ mb: 3 }} />
                        <Typography level="h4">Vendors</Typography>
                        <EditModeProvider defaultEdit={false}>
                            <VendorList
                                name="Vendor"
                                hideTitle
                                disabled={formDisabled}
                                vendors={listing.vendors.map(v => ({
                                    ...v,
                                    id: v.email,
                                }))}
                                removeVendor={() => {}}
                                vendorType={listing.vendors[0]?.vendorType ?? 'individual'}
                                setVendorType={() => {}}
                                showOccupantStatus={false}
                            />
                        </EditModeProvider>
                        <Divider sx={{ mb: 3 }} />
                        <SectionHead>Vendor Solicitor</SectionHead>
                        <Stack
                            gap={2}
                            sx={{ width: '500px' }}
                        >
                            <EditSolicitor
                                control={control}
                                solicitor="vendorSolicitor"
                                disabled={formDisabled}
                            />
                        </Stack>
                        <Divider sx={{ mb: 3 }} />
                        <Typography level="h4">Buyers</Typography>
                        <Stack gap={2}>
                            <VendorList
                                name="Buyer"
                                hideTitle
                                disabled={formDisabled}
                                vendors={buyers}
                                removeVendor={removeBuyer}
                                vendorType={vendorType}
                                setVendorType={setVendorType}
                                showOccupantStatus={false}
                            />
                            {buyers.length < 6 && !formDisabled && editMode && (
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        variant="soft"
                                        onClick={() =>
                                            appendBuyer({
                                                vendorType: vendorType as any,
                                                firstName: '',
                                                lastName: '',
                                                email: '',
                                                mobileNumber: '',
                                            })
                                        }
                                        startDecorator={<AddRounded />}
                                    >
                                        Add Buyer
                                    </Button>
                                </Box>
                            )}
                        </Stack>

                        {editMode ||
                            (buyersAgent && (
                                <>
                                    <Stack
                                        direction="row"
                                        gap={2}
                                        sx={{
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <SectionHead>Buyers Agent</SectionHead>
                                        <Checkbox
                                            checked={buyersAgent != undefined}
                                            onChange={e => {
                                                setValue('hasBuyersAgent', e.target.checked, {
                                                    shouldDirty: true,
                                                })

                                                if (e.target.checked) {
                                                    setValue('buyersAgent', {
                                                        name: '',
                                                    })
                                                } else {
                                                    setValue('buyersAgent', undefined)
                                                }
                                            }}
                                            sx={{
                                                mb: '3px',
                                            }}
                                            disabled={formDisabled}
                                        />
                                    </Stack>
                                    {buyersAgent && (
                                        <>
                                            <Controller
                                                name="buyersAgent.name"
                                                control={control}
                                                disabled={formDisabled}
                                                render={field => (
                                                    <SlotInput
                                                        label="Name"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="buyersAgent.email"
                                                control={control}
                                                disabled={formDisabled}
                                                render={field => (
                                                    <SlotInput
                                                        label="Email"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="buyersAgent.mobileNumber"
                                                control={control}
                                                disabled={formDisabled}
                                                render={field => (
                                                    <SlotInput
                                                        label="Mobile Number"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="buyersAgent.licenceNumber"
                                                control={control}
                                                disabled={formDisabled}
                                                render={field => (
                                                    <SlotInput
                                                        label="Licence Number"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="buyersAgent.abn"
                                                control={control}
                                                disabled={formDisabled}
                                                render={field => (
                                                    <SlotInput
                                                        label="ABN"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </>
                                    )}
                                </>
                            ))}
                        <Divider sx={{ mb: 3 }} />
                        <SectionHead>Buyer Solicitor</SectionHead>
                        <Stack
                            gap={2}
                            sx={{ width: '500px' }}
                        >
                            <EditSolicitor
                                control={control}
                                solicitor={'buyerSolicitor'}
                                disabled={formDisabled}
                            />
                        </Stack>
                        <Divider sx={{ mb: 3 }} />
                        <SectionHead>Deposits</SectionHead>
                        <Box sx={{ maxWidth: '500px ' }}>
                            <MobileFriendlyStack>
                                <Controller
                                    name="initialDeposit"
                                    disabled={formDisabled}
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label="Initial Deposit"
                                            type="dollar"
                                            startDecorator="$"
                                            {...field}
                                            formControlProps={{
                                                sx: { flex: 1 },
                                                width: '50px',
                                            }}
                                            fullWidth
                                        />
                                    )}
                                />
                                <Controller
                                    name="initialDepositDue"
                                    disabled={formDisabled}
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label="Due (days from contract)"
                                            //type='date'
                                            type="number"
                                            {...field}
                                            formControlProps={{
                                                sx: { flex: 1.8 },
                                            }}
                                            fullWidth
                                        />
                                    )}
                                />
                                <Input
                                    label="Due Date"
                                    type="date"
                                    value={
                                        contractDate && initialDepositDue
                                            ? getDueDate(new Date(contractDate), initialDepositDue)
                                            : undefined
                                    }
                                    formControlProps={{
                                        sx: { flex: 1 },
                                    }}
                                    readOnly
                                    disabled
                                    fullWidth
                                />
                                <DepositPaidRender depositNum={1} />
                            </MobileFriendlyStack>
                            <MobileFriendlyStack sx={{ mt: 1 }}>
                                <Controller
                                    name="balanceDeposit"
                                    disabled={formDisabled}
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label="Balance Deposit"
                                            type="dollar"
                                            startDecorator="$"
                                            {...field}
                                            formControlProps={{
                                                sx: { flex: 1 },
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="balanceDepositDue"
                                    disabled={formDisabled}
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label="Due (days from contract)"
                                            //type='date'
                                            type="number"
                                            {...field}
                                            formControlProps={{
                                                sx: { flex: 1.8 },
                                            }}
                                        />
                                    )}
                                />
                                <Input
                                    label="Due Date"
                                    type="date"
                                    value={getDueDate(
                                        contractDate ? new Date(contractDate) : undefined,
                                        balanceDepositDue,
                                    )}
                                    formControlProps={{
                                        sx: { flex: 1 },
                                    }}
                                    readOnly
                                    disabled
                                    fullWidth
                                />
                                <DepositPaidRender depositNum={2} />
                            </MobileFriendlyStack>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Typography level="h4">Settlement</Typography>
                        <Stack
                            gap={2}
                            sx={{ width: '500px' }}
                        >
                            <Controller
                                name="settlementDateDays"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Settlement Date (days from contract)"
                                        type="number"
                                        {...field}
                                        formControlProps={{
                                            sx: { flex: 1 },
                                        }}
                                    />
                                )}
                            />
                            <Input
                                label="Settlement Date"
                                type="date"
                                value={
                                    contractDate && settlementDateDays
                                        ? format(
                                              addDays(new Date(contractDate), settlementDateDays),
                                              'yyyy-MM-dd',
                                          )
                                        : undefined
                                }
                                onChange={val =>
                                    setValue(
                                        'settlementDateDays',
                                        differenceInDays(new Date(val), new Date(contractDate!)),
                                        { shouldDirty: true },
                                    )
                                }
                                formControlProps={{
                                    sx: { flex: 1 },
                                }}
                                fullWidth
                                disabled={!contractDate || formDisabled}
                            />
                        </Stack>

                        <Divider sx={{ mb: 3 }} />
                        <Typography level="h4">Conditions</Typography>
                        <Stack
                            gap={2}
                            sx={{ maxWidth: '700px ' }}
                        >
                            {editMode && (
                                <>
                                    <Box>
                                        <Controller
                                            name="requiresFinance"
                                            control={control}
                                            disabled={formDisabled}
                                            render={({ field }) => (
                                                <Checkbox
                                                    label="Requires Finance"
                                                    {...omit(['value'], field)}
                                                    checked={requiresFinance}
                                                    onChange={e => {
                                                        setValue('requiresFinance', e.target.checked)
                                                        if (e.target.checked) {
                                                            appendCondition({
                                                                condition: 'Subject to Finance',
                                                                dueDaysFromContract: 7,
                                                                fulfilled: false,
                                                                special: 'requiresFinance',
                                                            })
                                                        } else {
                                                            removeCondition(
                                                                conditions.findIndex(
                                                                    x => x.special == 'requiresFinance',
                                                                ),
                                                            )
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box>
                                        <Controller
                                            name="buildingAndPestRequired"
                                            control={control}
                                            disabled={formDisabled}
                                            render={({ field }) => (
                                                <Checkbox
                                                    label="Building & Pest Required"
                                                    {...omit(['value'], field)}
                                                    checked={buildingAndPestRequired}
                                                    onChange={e => {
                                                        setValue('buildingAndPestRequired', e.target.checked)
                                                        if (e.target.checked) {
                                                            appendCondition({
                                                                condition: 'Building & Pest',
                                                                dueDaysFromContract: 7,
                                                                fulfilled: false,
                                                                special: 'buildingAndPestRequired',
                                                            })
                                                        } else {
                                                            removeCondition(
                                                                conditions.findIndex(
                                                                    x =>
                                                                        x.special ==
                                                                        'buildingAndPestRequired',
                                                                ),
                                                            )
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </>
                            )}
                            {conditions.map((condition, i) => (
                                <ConditionRender
                                    key={condition.id}
                                    index={i}
                                    remove={() => removeCondition(i)}
                                    contractDate={contractDate ? new Date(contractDate) : undefined}
                                    formDisabled={formDisabled}
                                />
                            ))}
                            {!formDisabled && editMode && (
                                <Box>
                                    <Button
                                        variant="soft"
                                        startDecorator={<AddRounded />}
                                        onClick={() =>
                                            appendCondition({
                                                condition: '',
                                                dueDaysFromContract: 7,
                                                fulfilled: false,
                                            })
                                        }
                                    >
                                        Add Condition
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                    <Box sx={{ mt: 2, width: '500px' }}>
                        <Controller
                            name="snippets"
                            control={control}
                            render={({ field }) => (
                                <FormControl>
                                    <FormLabel>Condition Snippets</FormLabel>
                                    <Select
                                        {...field}
                                        multiple
                                        onChange={(e, value) => field.onChange(value)}
                                        disabled={formDisabled}
                                        renderNewLines
                                    >
                                        {snippetsApi.maybeData
                                            .map(snippets =>
                                                snippets.map(snippet => (
                                                    <Option
                                                        key={snippet.id}
                                                        value={snippet.id}
                                                    >
                                                        {snippet.snippetName}
                                                    </Option>
                                                )),
                                            )
                                            .orSome([])}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Box>
                    <Divider sx={{ mt: 2, mb: 5 }} />
                    <RenderListingValues tab="contract" />
                    {/*user.superUser && user.lastName == 'Bleier' && (
                        <Box>
                            <Button
                                color="danger"
                                loading={ocrLoading}
                                onClick={async () => {
                                    setOcrLoading(true)
                                    await api.post(`/listing/sale/${listing.listingId}/contractform/azureocr`)
                                    setOcrLoading(false)
                                }}
                                sx={{ mb: 2 }}
                            >
                                OCR
                            </Button>
                        </Box>
                    )*/}
                    <Divider sx={{ mt: 2, mb: 5 }} />
                    <Box sx={{ mb: 4 }}>
                        <DocusignFormStepper
                            title="Contract Form"
                            formType="contractForm"
                            signingService="annature"
                            createDisabled={
                                listing.vendors.length == 0 ||
                                listing.agents.length == 0 ||
                                listing.contract == undefined ||
                                listing.contract?.buyers.length == 0 ||
                                formFullyDisabled
                            }
                            validate={async () => {
                                console.log('validate', formState.isDirty)
                                if (formState.isDirty) {
                                    return await trigger()
                                }
                                return true
                            }}
                            preCreate={async () => {
                                try {
                                    return handleSubmitPromise()
                                } catch (e) {
                                    return false
                                }
                            }}
                            roleNames={[
                                ...buyers.map((buyer, i) => `${buyer.firstName} ${buyer.lastName}`),
                                ...listing.vendors.map(
                                    (vendor, i) => `${vendor.firstName} ${vendor.lastName}`,
                                ),
                            ]}
                        />
                    </Box>
                    <UnsavedModal
                        blocker={blocker}
                        save={(onSuccess, onFail) => saveForm().then(x => (x ? onSuccess() : onFail()))}
                    />
                </form>
            </FormProvider>
        </>
    )
})

export default Contract
